import {
  FLYER_ICONS,
  flyerIconSchema,
  PRODUCT_FEATURE_LIMITS,
  PRODUCT_LIST_LIMITS,
  type FlyerIcon,
  type ProductContent,
  type ProductTextList,
} from '@avrahami/contracts';
import type { ReactNode } from 'react';
import { Badge, Button, Card, SelectField, TextField, type BadgeTone } from './components';
import { FLYER_ICON_PATHS } from './flyer/icons';

/**
 * The strengths, "ideal for" lines and care instructions of a product's page (adr/0055), as the
 * store's admin and the platform staff edit them. A list the store edited by hand is the store's
 * own: no rewrite of the engine replaces it until the platform gives it back.
 */

export interface FeatureDraft {
  icon: FlyerIcon;
  title: string;
  text: string;
}

/** The lists of a product's page, as typed. */
export interface ProductListsDraft {
  features: readonly FeatureDraft[];
  idealFor: readonly string[];
  care: readonly string[];
}

export function productListsDraft(
  content: Pick<ProductContent, ProductTextList> | undefined,
): ProductListsDraft {
  return {
    features: content?.features ?? [],
    idealFor: content?.idealFor ?? [],
    care: content?.care ?? [],
  };
}

/** Lines left empty are dropped. */
function lines(list: readonly string[]): string[] {
  return list.map((line) => line.trim()).filter((line) => line !== '');
}

/** Strengths left completely empty are dropped; half-filled ones are kept (and rejected). */
function features(list: readonly FeatureDraft[]): FeatureDraft[] {
  return list
    .map((feature) => ({
      icon: feature.icon,
      title: feature.title.trim(),
      text: feature.text.trim(),
    }))
    .filter((feature) => feature.title !== '' || feature.text !== '');
}

/**
 * The lists of `draft` that differ from `base` (the ones the draft was made from), cleaned: a list
 * sent becomes the store's own, so an untouched one is never sent, and neither is one the engine
 * rewrote after the draft was made.
 */
export function changedProductLists(
  draft: ProductListsDraft,
  base: ProductListsDraft,
): Partial<{ features: FeatureDraft[]; idealFor: string[]; care: string[] }> {
  const now = {
    features: features(draft.features),
    idealFor: lines(draft.idealFor),
    care: lines(draft.care),
  };
  const before = {
    features: features(base.features),
    idealFor: lines(base.idealFor),
    care: lines(base.care),
  };
  const same = (list: ProductTextList) =>
    JSON.stringify(now[list]) === JSON.stringify(before[list]);
  return {
    ...(same('features') ? {} : { features: now.features }),
    ...(same('idealFor') ? {} : { idealFor: now.idealFor }),
    ...(same('care') ? {} : { care: now.care }),
  };
}

type ListState = 'EDITED' | 'AUTO' | 'PENDING' | 'WRITING';

/** The words of the lists' card, from the app's own dictionary. */
export interface ProductListsWords {
  title: string;
  /** Under the title, on a catalog product. */
  note: string;
  names: Record<ProductTextList, string>;
  states: Record<ListState, string>;
  empty: string;
  /** `{count}`: the most lines of the list. */
  limit: string;
  featureIcon: string;
  featureTitle: string;
  featureText: string;
  idealForLine: string;
  careLine: string;
  addLine: string;
  removeLine: string;
  icons: Record<FlyerIcon, string>;
}

/** The words of "write again from the catalog" (the platform's admin only). */
export interface RestoreListWords {
  label: string;
  hint: string;
  unsaved: string;
}

const stateTones = {
  EDITED: 'green',
  AUTO: 'blue',
  PENDING: 'amber',
  WRITING: 'amber',
} as const satisfies Record<ListState, BadgeTone>;

/** Where a list's lines come from now (adr/0055). */
function stateOf(content: ProductContent, list: ProductTextList): ListState {
  if (content.editedLists.includes(list)) return 'EDITED';
  if (content.writing) return 'WRITING';
  return content[list] === null ? 'PENDING' : 'AUTO';
}

/** The first message under a path (`content.care` covers `content.care.2`). */
function errorUnder(errors: Readonly<Record<string, string>>, prefix: string): string | undefined {
  const key = Object.keys(errors).find((path) => path === prefix || path.startsWith(`${prefix}.`));
  return key === undefined ? undefined : errors[key];
}

function FeatureIcon({ icon }: { icon: FlyerIcon }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="mb-2 size-6 shrink-0 text-brand-700"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {FLYER_ICON_PATHS[icon].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** One list: its name, where its lines come from, the lines, and "write again from the catalog". */
function ListSection({
  words,
  list,
  state,
  count,
  max,
  restore,
  error,
  onAdd,
  children,
}: {
  words: ProductListsWords;
  list: ProductTextList;
  /** Null for a product of the store's own: it has no engine. */
  state: ListState | null;
  count: number;
  max: number;
  restore: {
    words: RestoreListWords;
    busy: boolean;
    blocked: boolean;
    onRestore: () => void;
  } | null;
  error: string | undefined;
  onAdd: () => void;
  children: ReactNode;
}) {
  return (
    <fieldset
      className="flex flex-col gap-3 border-t border-slate-200 pt-4 first:border-t-0 first:pt-0"
      data-testid={`product-list-${list}`}
      data-state={state ?? 'OWN'}
    >
      <legend className="contents">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">{words.names[list]}</span>
          {state === null ? null : <Badge tone={stateTones[state]}>{words.states[state]}</Badge>}
          <span className="text-xs text-slate-500">
            {words.limit.replace('{count}', String(max))}
          </span>
        </span>
      </legend>
      {count === 0 ? <p className="text-sm text-slate-500">{words.empty}</p> : children}
      {error === undefined ? null : (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button tone="secondary" disabled={count >= max} onClick={onAdd}>
          {words.addLine}
        </Button>
        {restore === null ? null : (
          <>
            <Button
              tone="secondary"
              busy={restore.busy}
              disabled={restore.blocked}
              data-testid={`restore-${list}`}
              onClick={restore.onRestore}
            >
              {restore.words.label}
            </Button>
            <span className="text-xs text-slate-500">
              {restore.blocked ? restore.words.unsaved : restore.words.hint}
            </span>
          </>
        )}
      </div>
    </fieldset>
  );
}

/** Lines of text with add and remove: the "ideal for" lines or the care instructions. */
function LineRows({
  words,
  label,
  rows,
  maxLength,
  onChange,
}: {
  words: ProductListsWords;
  label: string;
  rows: readonly string[];
  maxLength: number;
  onChange: (rows: string[]) => void;
}) {
  return (
    <ol className="flex flex-col gap-2">
      {rows.map((row, index) => (
        <li key={index} className="flex flex-wrap items-end gap-2">
          <TextField
            label={`${label} ${String(index + 1)}`}
            value={row}
            maxLength={maxLength}
            className="min-w-48 flex-1"
            onChange={(event) => {
              const value = event.target.value;
              onChange(rows.map((line, at) => (at === index ? value : line)));
            }}
          />
          <Button
            tone="secondary"
            aria-label={`${words.removeLine} ${label} ${String(index + 1)}`}
            onClick={() => {
              onChange(rows.filter((_line, at) => at !== index));
            }}
          >
            {words.removeLine}
          </Button>
        </li>
      ))}
    </ol>
  );
}

function FeatureRows({
  words,
  rows,
  onChange,
}: {
  words: ProductListsWords;
  rows: readonly FeatureDraft[];
  onChange: (rows: FeatureDraft[]) => void;
}) {
  const update = (index: number, change: Partial<FeatureDraft>) => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  };
  return (
    <ol className="flex flex-col gap-3">
      {rows.map((row, index) => (
        <li
          key={index}
          className="flex flex-wrap items-end gap-2 rounded-md p-2 ring-1 ring-slate-200"
          data-testid="feature-row"
        >
          <FeatureIcon icon={row.icon} />
          <SelectField
            label={`${words.featureIcon} ${String(index + 1)}`}
            value={row.icon}
            className="w-40"
            onChange={(event) => {
              update(index, { icon: flyerIconSchema.parse(event.target.value) });
            }}
          >
            {FLYER_ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {words.icons[icon]}
              </option>
            ))}
          </SelectField>
          <TextField
            label={`${words.featureTitle} ${String(index + 1)}`}
            value={row.title}
            maxLength={PRODUCT_FEATURE_LIMITS.title}
            className="w-56"
            onChange={(event) => {
              update(index, { title: event.target.value });
            }}
          />
          <TextField
            label={`${words.featureText} ${String(index + 1)}`}
            value={row.text}
            maxLength={PRODUCT_FEATURE_LIMITS.text}
            className="min-w-56 flex-1"
            onChange={(event) => {
              update(index, { text: event.target.value });
            }}
          />
          <Button
            tone="secondary"
            aria-label={`${words.removeLine} ${words.featureTitle} ${String(index + 1)}`}
            onClick={() => {
              onChange(rows.filter((_row, at) => at !== index));
            }}
          >
            {words.removeLine}
          </Button>
        </li>
      ))}
    </ol>
  );
}

/**
 * The card of the three lists. On a catalog product (`catalog`) each says whether the engine
 * writes it or the store edited it. With `restore` (the platform staff only), a list the store
 * edited can be given back to the engine, outside the form's save.
 */
export function ProductListsCard({
  words,
  draft,
  set,
  errors,
  content,
  catalog,
  restore,
}: {
  words: ProductListsWords;
  draft: ProductListsDraft;
  set: (change: Partial<ProductListsDraft>) => void;
  errors: Readonly<Record<string, string>>;
  content: ProductContent;
  catalog: boolean;
  restore?: {
    words: RestoreListWords;
    /** The form has changes that are not saved: giving a list back would drop them. */
    unsaved: boolean;
    restoring: ProductTextList | null;
    onRestore: (list: ProductTextList) => void;
  };
}) {
  const sectionOf = (list: ProductTextList) => ({
    words,
    list,
    state: catalog ? stateOf(content, list) : null,
    error: errorUnder(errors, `content.${list}`),
    restore:
      catalog && restore !== undefined && content.editedLists.includes(list)
        ? {
            words: restore.words,
            busy: restore.restoring === list,
            blocked: restore.unsaved || restore.restoring !== null,
            onRestore: () => {
              restore.onRestore(list);
            },
          }
        : null,
  });
  return (
    <Card title={words.title}>
      <div className="flex flex-col gap-4" data-testid="product-lists">
        {catalog ? <p className="text-xs text-slate-500">{words.note}</p> : null}
        <ListSection
          {...sectionOf('features')}
          count={draft.features.length}
          max={PRODUCT_FEATURE_LIMITS.max}
          onAdd={() => {
            set({ features: [...draft.features, { icon: 'QUALITY', title: '', text: '' }] });
          }}
        >
          <FeatureRows
            words={words}
            rows={draft.features}
            onChange={(next) => {
              set({ features: next });
            }}
          />
        </ListSection>
        <ListSection
          {...sectionOf('idealFor')}
          count={draft.idealFor.length}
          max={PRODUCT_LIST_LIMITS.idealFor.max}
          onAdd={() => {
            set({ idealFor: [...draft.idealFor, ''] });
          }}
        >
          <LineRows
            words={words}
            label={words.idealForLine}
            rows={draft.idealFor}
            maxLength={PRODUCT_LIST_LIMITS.idealFor.line}
            onChange={(idealFor) => {
              set({ idealFor });
            }}
          />
        </ListSection>
        <ListSection
          {...sectionOf('care')}
          count={draft.care.length}
          max={PRODUCT_LIST_LIMITS.care.max}
          onAdd={() => {
            set({ care: [...draft.care, ''] });
          }}
        >
          <LineRows
            words={words}
            label={words.careLine}
            rows={draft.care}
            maxLength={PRODUCT_LIST_LIMITS.care.line}
            onChange={(care) => {
              set({ care });
            }}
          />
        </ListSection>
      </div>
    </Card>
  );
}
